import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Palette, CheckCircle2, AlertCircle, Loader2, RotateCcw, Package, History, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const STATUS_COLORS = {
  PRESSED:   'bg-fuchsia-100 text-fuchsia-700',
  PROCESSED: 'bg-green-200 text-green-900',
};

const fmt = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ColorAssortmentEntry = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [scan, setScan] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { loadRecent(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const loadRecent = async () => {
    try {
      const r = await apiClient.get('/inhouse/color-assortment/recent', { params: { limit: 20 } });
      setRecent(r.data?.rows || []);
    } catch { /* non-blocking */ }
  };

  const runLookup = async () => {
    const code = scan.trim();
    if (code.length < 2) { toast.error('Enter at least 2 characters'); return; }
    setLookupLoading(true);
    setError('');
    setLookup(null);
    try {
      const r = await apiClient.get('/inhouse/color-assortment/lookup', { params: { bundle_code: code } });
      setLookup(r.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Lookup failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLookupLoading(false);
    }
  };

  const onScanKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); runLookup(); }
  };

  const submit = async () => {
    if (!lookup?.bundle_code) return;
    setSubmitting(true);
    try {
      const r = await apiClient.post('/inhouse/color-assortment/create', {
        bundle_code: lookup.bundle_code,
        reason: reason.trim() || null,
      });
      toast.success(r.data?.message || 'Bundle sent to Color Assortment');
      setScan('');
      setLookup(null);
      setReason('');
      setError('');
      inputRef.current?.focus();
      loadRecent();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send to Color Assortment';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setScan(''); setLookup(null); setReason(''); setError('');
    inputRef.current?.focus();
  };

  const existing = lookup?.existing_inhouse_record;
  const alreadyDone = existing && existing.flow_type === 'COLOR_ASSORTMENT'
    && ['PRESSED', 'PROCESSED'].includes((existing.status || '').toUpperCase());

  return (
    <div className="min-h-screen bg-gray-50" data-testid="color-assortment-entry-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10 pr-28 sm:pr-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-fuchsia-600" />
          <div>
            <h1 className="text-lg font-bold text-gray-900">Color Assortment Entry</h1>
            <p className="text-xs text-gray-500">Scan a rework bundle to send it straight to Assortment Packing (skips Folding, Pressing, Segregation & Damage)</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        {/* Skip-flow hint */}
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
          <Badge className="bg-fuchsia-600 text-white border-0">Scan Bundle</Badge>
          <ArrowRight className="w-3 h-3" />
          <span className="line-through text-gray-400">Folding · Pressing · Segregation · Damage</span>
          <ArrowRight className="w-3 h-3" />
          <Badge className="bg-emerald-600 text-white border-0">Assortment Packing</Badge>
        </div>

        {/* Scan card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-500" />
              Scan Bundle Code
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={scan}
                onChange={(e) => setScan(e.target.value)}
                onKeyDown={onScanKeyDown}
                placeholder="e.g. BUN012345/26-27"
                className="font-mono text-base"
                disabled={lookupLoading || submitting}
                data-testid="bundle-scan-input"
                autoFocus
              />
              <Button
                onClick={runLookup}
                disabled={lookupLoading || submitting || scan.trim().length < 2}
                data-testid="lookup-btn"
              >
                {lookupLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Looking up</>
                ) : (
                  <><Search className="w-4 h-4 mr-2" /> Lookup</>
                )}
              </Button>
              {(lookup || error) && (
                <Button variant="outline" onClick={reset} data-testid="reset-btn">
                  <RotateCcw className="w-4 h-4" />
                </Button>
              )}
            </div>
            {error && (
              <Alert className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700 text-sm" data-testid="lookup-error">{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Lookup result */}
        {lookup && (
          <Card className="border-fuchsia-200" data-testid="lookup-result">
            <CardHeader className="pb-2 bg-fuchsia-50/50">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="w-4 h-4 text-fuchsia-600" />
                  Bundle Details
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    Source: {lookup.current_section}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {lookup.source}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Bundle Code</p>
                  <p className="font-mono font-semibold">{lookup.bundle_code}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">USNs</p>
                  <p className="font-bold text-fuchsia-700 text-lg" data-testid="lookup-usn-count">{lookup.usn_count}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Design</p>
                  <p className="font-medium">{lookup.design_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Shade</p>
                  <p className="font-medium">{lookup.shade || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sort No</p>
                  <p className="font-medium">{lookup.sort_no || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">SKU</p>
                  <p className="font-mono text-xs">{lookup.sku_code || '-'}</p>
                </div>
                {lookup.current_status && (
                  <div>
                    <p className="text-xs text-gray-500">Current Status (source)</p>
                    <p className="font-medium text-xs">{lookup.current_status}</p>
                  </div>
                )}
              </div>

              {existing && (
                <Alert className={alreadyDone ? "bg-amber-50 border-amber-300" : "bg-blue-50 border-blue-200"}>
                  <AlertCircle className={`h-4 w-4 ${alreadyDone ? 'text-amber-600' : 'text-blue-600'}`} />
                  <AlertDescription className="text-sm">
                    <span className="font-medium">Existing Inhouse record:</span>{' '}
                    flow_type=<code>{existing.flow_type}</code>, status=<code>{existing.status}</code>
                    {existing.created_at && <> (created {fmt(existing.created_at)})</>}.
                    {alreadyDone && (
                      <div className="mt-1 text-amber-800">
                        ⚠️ This bundle is already in Color Assortment and is ready in Assortment Packing.
                        Submitting will be blocked.
                      </div>
                    )}
                    {!alreadyDone && (
                      <div className="mt-1 text-blue-800">
                        Submitting will mark this bundle COLOR_ASSORTMENT and make all its pieces ready in Assortment Packing.
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {lookup.usn_count === 0 && (
                <Alert className="bg-red-50 border-red-200">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-sm text-red-700">
                    This bundle has no minted USNs in {lookup.source}. Color Assortment
                    requires pre-minted USNs — use Bundle Receipt Entry for fresh bundles.
                  </AlertDescription>
                </Alert>
              )}

              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">
                  Reason for Color Assortment <span className="text-gray-400">(optional)</span>
                </label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. shade mismatch, re-assortment, customer swap…"
                  data-testid="reason-input"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="outline" onClick={reset} disabled={submitting} data-testid="cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  disabled={submitting || lookup.usn_count === 0 || alreadyDone}
                  className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                  data-testid="confirm-color-assortment-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Send to Assortment Packing</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent */}
        {recent.length > 0 && (
          <Card data-testid="recent-color-assortments-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <History className="w-4 h-4 text-gray-500" />
                Recent Color Assortment Entries ({recent.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bundle</TableHead>
                    <TableHead>Design / Shade</TableHead>
                    <TableHead>Sort</TableHead>
                    <TableHead className="text-center">USNs</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Marked</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent.map((r) => (
                    <TableRow key={r.bundle_code} data-testid={`recent-row-${r.bundle_code}`}>
                      <TableCell className="font-mono text-xs">{r.bundle_code}</TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium">{r.design_name || '-'}</div>
                        <div className="text-gray-500">{r.shade || ''}</div>
                      </TableCell>
                      <TableCell className="text-xs">{r.sort_no || '-'}</TableCell>
                      <TableCell className="text-center font-semibold">{r.usn_count || 0}</TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-700'} border-0 text-[10px]`}>
                          {r.status || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{r.color_assortment_source_section || '-'}</TableCell>
                      <TableCell className="text-xs">
                        <div>{fmt(r.color_assortment_marked_at)}</div>
                        {r.color_assortment_marked_by_name && (
                          <div className="text-gray-400 text-[10px]">{r.color_assortment_marked_by_name}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ColorAssortmentEntry;
